import { useRef } from "react"
import * as THREE from "three"
import { useFrame } from "@react-three/fiber"
import { RoundedBox } from "@react-three/drei"

const wood = "#8B6914"
const darkWood = "#5C4033"
const metal = "#2a2a2a"
const darkGray = "#1a1a1a"
const lightGray = "#999"
const white = "#f5f5f0"
const screenBlue = "#1a3a5c"
const green = "#2d5a27"
const potColor = "#8B4513"
const fabricGray = "#3a3a3a"
const cushion = "#444"

export function Desk(props) {
  return (
    <group {...props}>
      {/* Desktop surface */}
      <mesh position={[0, 0.74, 0]} castShadow receiveShadow>
        <boxGeometry args={[1.6, 0.04, 0.8]} />
        <meshStandardMaterial color={wood} roughness={0.3} />
      </mesh>
      {/* Front left leg */}
      <mesh position={[-0.72, 0.37, 0.34]}>
        <boxGeometry args={[0.05, 0.74, 0.05]} />
        <meshStandardMaterial color={metal} metalness={0.6} roughness={0.3} />
      </mesh>
      {/* Front right leg */}
      <mesh position={[0.72, 0.37, 0.34]}>
        <boxGeometry args={[0.05, 0.74, 0.05]} />
        <meshStandardMaterial color={metal} metalness={0.6} roughness={0.3} />
      </mesh>
      {/* Back left leg */}
      <mesh position={[-0.72, 0.37, -0.34]}>
        <boxGeometry args={[0.05, 0.74, 0.05]} />
        <meshStandardMaterial color={metal} metalness={0.6} roughness={0.3} />
      </mesh>
      {/* Back right leg */}
      <mesh position={[0.72, 0.37, -0.34]}>
        <boxGeometry args={[0.05, 0.74, 0.05]} />
        <meshStandardMaterial color={metal} metalness={0.6} roughness={0.3} />
      </mesh>
      {/* Drawer unit */}
      <mesh position={[0.45, 0.45, 0]}>
        <boxGeometry args={[0.45, 0.5, 0.65]} />
        <meshStandardMaterial color={white} roughness={0.5} />
      </mesh>
      {/* Drawer handles */}
      <mesh position={[0.45, 0.55, 0.33]}>
        <boxGeometry args={[0.15, 0.02, 0.02]} />
        <meshStandardMaterial color={metal} metalness={0.8} roughness={0.2} />
      </mesh>
      <mesh position={[0.45, 0.38, 0.33]}>
        <boxGeometry args={[0.15, 0.02, 0.02]} />
        <meshStandardMaterial color={metal} metalness={0.8} roughness={0.2} />
      </mesh>
    </group>
  )
}

export function Chair(props) {
  return (
    <group {...props}>
      {/* Seat */}
      <mesh position={[0, 0.48, 0]} castShadow>
        <boxGeometry args={[0.5, 0.08, 0.5]} />
        <meshStandardMaterial color={cushion} roughness={0.8} />
      </mesh>
      {/* Backrest */}
      <mesh position={[0, 0.78, -0.22]} castShadow>
        <boxGeometry args={[0.48, 0.55, 0.06]} />
        <meshStandardMaterial color={fabricGray} roughness={0.9} />
      </mesh>
      {/* Center pole */}
      <mesh position={[0, 0.27, 0]}>
        <cylinderGeometry args={[0.03, 0.03, 0.38, 8]} />
        <meshStandardMaterial color={metal} metalness={0.7} roughness={0.2} />
      </mesh>
      {/* Base star - 5 legs */}
      {[0, 1, 2, 3, 4].map((i) => (
        <mesh key={i} position={[Math.sin((i * Math.PI * 2) / 5) * 0.25, 0.06, Math.cos((i * Math.PI * 2) / 5) * 0.25]} rotation={[0, (i * Math.PI * 2) / 5, 0]}>
          <boxGeometry args={[0.04, 0.04, 0.25]} />
          <meshStandardMaterial color={metal} metalness={0.7} roughness={0.2} />
        </mesh>
      ))}
      {/* Wheels */}
      {[0, 1, 2, 3, 4].map((i) => (
        <mesh key={`w${i}`} position={[Math.sin((i * Math.PI * 2) / 5) * 0.3, 0.025, Math.cos((i * Math.PI * 2) / 5) * 0.3]}>
          <sphereGeometry args={[0.025, 8, 8]} />
          <meshStandardMaterial color={darkGray} />
        </mesh>
      ))}
      {/* Armrests */}
      <mesh position={[-0.28, 0.62, -0.05]}>
        <boxGeometry args={[0.04, 0.22, 0.04]} />
        <meshStandardMaterial color={metal} metalness={0.5} roughness={0.3} />
      </mesh>
      <mesh position={[0.28, 0.62, -0.05]}>
        <boxGeometry args={[0.04, 0.22, 0.04]} />
        <meshStandardMaterial color={metal} metalness={0.5} roughness={0.3} />
      </mesh>
      <mesh position={[-0.28, 0.72, 0.02]}>
        <boxGeometry args={[0.06, 0.03, 0.2]} />
        <meshStandardMaterial color={cushion} roughness={0.8} />
      </mesh>
      <mesh position={[0.28, 0.72, 0.02]}>
        <boxGeometry args={[0.06, 0.03, 0.2]} />
        <meshStandardMaterial color={cushion} roughness={0.8} />
      </mesh>
    </group>
  )
}

export function Monitor(props) {
  return (
    <group {...props}>
      {/* Screen */}
      <mesh position={[0, 1.05, 0]} castShadow>
        <boxGeometry args={[0.7, 0.42, 0.03]} />
        <meshStandardMaterial color={darkGray} roughness={0.3} />
      </mesh>
      {/* Screen face */}
      <mesh position={[0, 1.05, 0.016]}>
        <planeGeometry args={[0.64, 0.36]} />
        <meshStandardMaterial color={screenBlue} emissive={screenBlue} emissiveIntensity={0.3} roughness={0.1} />
      </mesh>
      {/* Stand neck */}
      <mesh position={[0, 0.85, -0.05]}>
        <boxGeometry args={[0.06, 0.12, 0.06]} />
        <meshStandardMaterial color={metal} metalness={0.7} roughness={0.2} />
      </mesh>
      {/* Stand base */}
      <mesh position={[0, 0.77, 0]}>
        <cylinderGeometry args={[0.15, 0.15, 0.02, 16]} />
        <meshStandardMaterial color={metal} metalness={0.7} roughness={0.2} />
      </mesh>
    </group>
  )
}

export function Keyboard(props) {
  return (
    <group {...props}>
      <mesh position={[0, 0.77, 0.2]}>
        <boxGeometry args={[0.4, 0.015, 0.14]} />
        <meshStandardMaterial color={darkGray} roughness={0.6} />
      </mesh>
      {/* Mouse */}
      <mesh position={[0.3, 0.77, 0.2]}>
        <boxGeometry args={[0.06, 0.02, 0.1]} />
        <meshStandardMaterial color={darkGray} roughness={0.4} />
      </mesh>
    </group>
  )
}

export function Bookshelf(props) {
  const shelfColor = darkWood
  return (
    <group {...props}>
      {/* Back panel */}
      <mesh position={[0, 0.9, -0.14]}>
        <boxGeometry args={[0.8, 1.8, 0.02]} />
        <meshStandardMaterial color={shelfColor} roughness={0.5} />
      </mesh>
      {/* Left side */}
      <mesh position={[-0.39, 0.9, 0]}>
        <boxGeometry args={[0.02, 1.8, 0.3]} />
        <meshStandardMaterial color={shelfColor} roughness={0.5} />
      </mesh>
      {/* Right side */}
      <mesh position={[0.39, 0.9, 0]}>
        <boxGeometry args={[0.02, 1.8, 0.3]} />
        <meshStandardMaterial color={shelfColor} roughness={0.5} />
      </mesh>
      {/* Shelves */}
      {[0, 0.45, 0.9, 1.35, 1.8].map((y, i) => (
        <mesh key={i} position={[0, y, 0]}>
          <boxGeometry args={[0.8, 0.03, 0.3]} />
          <meshStandardMaterial color={shelfColor} roughness={0.5} />
        </mesh>
      ))}
      {/* Books row 1 */}
      <Books position={[-0.15, 0.25, 0]} />
      {/* Books row 2 */}
      <Books position={[0.05, 0.7, 0]} count={4} />
      {/* Books row 3 */}
      <Books position={[-0.1, 1.15, 0]} count={5} />
      {/* Decorative box on top shelf */}
      <mesh position={[0.1, 1.55, 0]}>
        <boxGeometry args={[0.15, 0.12, 0.15]} />
        <meshStandardMaterial color="#c4a882" roughness={0.6} />
      </mesh>
    </group>
  )
}

function Books({ count = 6, ...props }) {
  const colors = ["#8B0000", "#00008B", "#006400", "#4B0082", "#8B4513", "#2F4F4F"]
  return (
    <group {...props}>
      {Array.from({ length: count }).map((_, i) => (
        <mesh key={i} position={[i * 0.065 - (count * 0.065) / 2, 0, 0]}>
          <boxGeometry args={[0.05, 0.3 + Math.random() * 0.1, 0.2]} />
          <meshStandardMaterial color={colors[i % colors.length]} roughness={0.8} />
        </mesh>
      ))}
    </group>
  )
}

export function DeskLamp(props) {
  return (
    <group {...props}>
      {/* Base */}
      <mesh position={[0, 0.77, 0]}>
        <cylinderGeometry args={[0.08, 0.1, 0.03, 16]} />
        <meshStandardMaterial color={metal} metalness={0.8} roughness={0.2} />
      </mesh>
      {/* Lower arm */}
      <mesh position={[0, 0.95, -0.02]} rotation={[0.15, 0, 0]}>
        <cylinderGeometry args={[0.012, 0.012, 0.35, 8]} />
        <meshStandardMaterial color={metal} metalness={0.7} roughness={0.2} />
      </mesh>
      {/* Upper arm */}
      <mesh position={[0.02, 1.15, 0.04]} rotation={[-0.5, 0, 0]}>
        <cylinderGeometry args={[0.012, 0.012, 0.3, 8]} />
        <meshStandardMaterial color={metal} metalness={0.7} roughness={0.2} />
      </mesh>
      {/* Shade */}
      <mesh position={[0.04, 1.25, 0.12]} rotation={[0.3, 0, 0]}>
        <coneGeometry args={[0.08, 0.12, 16, 1, true]} />
        <meshStandardMaterial color={metal} metalness={0.6} roughness={0.3} side={THREE.DoubleSide} />
      </mesh>
      {/* Light bulb glow */}
      <pointLight position={[0.04, 1.22, 0.12]} intensity={0.5} distance={2} color="#fff5e0" />
    </group>
  )
}

export function Plant(props) {
  return (
    <group {...props}>
      {/* Pot */}
      <mesh position={[0, 0.08, 0]}>
        <cylinderGeometry args={[0.08, 0.06, 0.16, 12]} />
        <meshStandardMaterial color={potColor} roughness={0.7} />
      </mesh>
      {/* Soil */}
      <mesh position={[0, 0.16, 0]}>
        <cylinderGeometry args={[0.075, 0.075, 0.02, 12]} />
        <meshStandardMaterial color="#3d2b1f" roughness={0.9} />
      </mesh>
      {/* Leaves */}
      {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
        <mesh key={i} position={[Math.sin((i * Math.PI) / 4) * 0.04, 0.25 + Math.random() * 0.12, Math.cos((i * Math.PI) / 4) * 0.04]} rotation={[Math.random() * 0.5 - 0.25, (i * Math.PI) / 4, Math.random() * 0.3]}>
          <planeGeometry args={[0.06, 0.15]} />
          <meshStandardMaterial color={green} side={THREE.DoubleSide} roughness={0.8} />
        </mesh>
      ))}
      {/* Stem */}
      <mesh position={[0, 0.22, 0]}>
        <cylinderGeometry args={[0.008, 0.008, 0.14, 6]} />
        <meshStandardMaterial color="#1a4a14" roughness={0.8} />
      </mesh>
    </group>
  )
}

export function Floor(props) {
  return (
    <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0, 0]} receiveShadow {...props}>
      <planeGeometry args={[8, 8]} />
      <meshStandardMaterial color="#c4b89a" roughness={0.8} />
    </mesh>
  )
}

export function Walls(props) {
  return (
    <group {...props}>
      {/* Back wall */}
      <mesh position={[0, 1.5, -2]} receiveShadow>
        <planeGeometry args={[8, 3]} />
        <meshStandardMaterial color={white} roughness={0.9} />
      </mesh>
      {/* Left wall */}
      <mesh position={[-3, 1.5, 0]} rotation={[0, Math.PI / 2, 0]} receiveShadow>
        <planeGeometry args={[8, 3]} />
        <meshStandardMaterial color="#eeeee8" roughness={0.9} />
      </mesh>
    </group>
  )
}

export function Rug(props) {
  return (
    <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.005, 0.3]} {...props}>
      <planeGeometry args={[2.2, 1.6]} />
      <meshStandardMaterial color="#4a6670" roughness={0.95} />
    </mesh>
  )
}

export function CoffeeMug(props) {
  return (
    <group {...props}>
      <mesh position={[0, 0.8, 0]}>
        <cylinderGeometry args={[0.035, 0.03, 0.08, 12]} />
        <meshStandardMaterial color={white} roughness={0.3} />
      </mesh>
      {/* Handle */}
      <mesh position={[0.045, 0.8, 0]} rotation={[0, 0, Math.PI / 2]}>
        <torusGeometry args={[0.025, 0.008, 8, 12, Math.PI]} />
        <meshStandardMaterial color={white} roughness={0.3} />
      </mesh>
      {/* Coffee */}
      <mesh position={[0, 0.835, 0]}>
        <cylinderGeometry args={[0.032, 0.032, 0.005, 12]} />
        <meshStandardMaterial color="#3d1c02" roughness={0.2} />
      </mesh>
    </group>
  )
}
